import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineArrowBack } from 'react-icons/md';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Button from 'components/Button2';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';

import './index.css';

const timePerQuestionEnglish = 35 * 60 / 50;
const timePerQuestionMath = 70 * 60 / 44;    
const timePerQuestionReading = 40 * 60 / 36;
const timePerQuestionScience = 40 * 60 / 36;
const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];

type DigitalACTReportProps = {
    // Bulk print mounts this component directly instead of routing to it, so
    // the test id has to be able to arrive as a prop as well as a URL param.
    testIdOverride?: string;
    // Rendered inside the bulk-print container: drop the page chrome (document
    // title, action buttons) so the sheet carries report content only.
    embedded?: boolean;
    // Fires once this report's data has settled, so a bulk print can wait for
    // every report to arrive before opening the print dialog.
    onReady?: () => void;
};

function DigitalACTReport({ testIdOverride, embedded = false, onReady }: DigitalACTReportProps = {}) {
    const [fontSize, setFontSize] = useState(80);
    const [testData, setTestData] = useState(null);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [moduleIndex, setModuleIndex] = useState(0);

    const { user } = useAuthContext();
    const { testId: routeTestId } = useParams();
    const navigate = useNavigate();
    const testId = testIdOverride ?? routeTestId;
    const printRef = useRef(null);

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        // Marks the print iframe's <body> so the question tables are revealed
        // for this single-report print only. Bulk print never sets it.
        bodyClass: 'printing-single-report',
    });

    // `onReady` is read through a ref so a parent passing a fresh closure each
    // render cannot retrigger the fetch -- the effect stays keyed on the id.
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        if (!testId) return;
        let cancelled = false;
        axios.get(`tests/digital/actResultTutor/${testId}/`)
            .then(res => {
                if (cancelled) return;
                setTestData(res.data);
                onReadyRef.current?.();
            })
            .catch(error => {
                console.log(error);
                // Report ready even on failure: one unreachable report must not
                // leave a bulk print waiting for a dialog that never opens.
                if (!cancelled) onReadyRef.current?.();
            });
        return () => { cancelled = true; };
    }, [testId]);

    useEffect(() => {
        const updateLayout = () => {
            const width = window.innerWidth;
            if (width > 1300) {
                setFontSize(80);
            } else {
                setFontSize(60);
            }
        };
    
        updateLayout(); // 초기 실행
        window.addEventListener('resize', updateLayout);
        return () => {
            window.removeEventListener('resize', updateLayout);
        };
    }, []);

    if (!testData) return <></>;

    return (
        <div className='testACTReport' ref={printRef}>
            {!embedded &&
                <Helmet>
                    <title>
                        Test Report - Test Center Admin
                    </title>
                </Helmet>
            }
            {!embedded && (
                <div className='testACTReport__bar'>
                    <button className='testACTReport__back' onClick={() => navigate(-1)}>
                        <MdOutlineArrowBack className='testACTReport__backIcon' />
                        Back
                    </button>
                </div>
            )}
            <div className='testACTReport__headerGroup'>
                <div className='testACTReport__headerSection'>
                    {testData.logo && 
                        <img
                            className='testACTReport__headerLogo'
                            alt='logo'
                            src={testData.logo}
                        />
                    }
                </div>
                <div className='testACTReport__headerSection'>
                    <div className='testACTReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Email: {testData.email}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='testACTReport__headerGroup'>
                <h1 className='testACTReport__headerTitle'>
                    Test Result Overview
                </h1>
                <div className='testACTReport__buttonGroup'>
                    <ButtonLink className='testACTReport__button--blue' to={`/review/digital-act/${testId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                    <Button className='testACTReport__button' secondary onClick={handlePrint}>
                        PRINT REPORT
                    </Button>
                    <ButtonLink className='testACTReport__button' to={`/students/${testData.studentId}/digital-act/performanceHistory`}>
                        PERFORMANCE HISTORY
                    </ButtonLink>
                </div>
            </div>
            <div className='testACTReport__card'>
                {/* test scores */}
                <div className='testACTReport__scoresGroup'>
                    {testData.compositeScore && <>
                        <div className='testACTReport__scoresGroupBox'>
                            <div className='testACTReport__scoreTitleBoxCom'>
                                <h3 className='testACTReport__scoreTitle'>COMPOSITE SCORE</h3>
                            </div>
                            <div className='testACTReport__scoreContent'>
                                <ScoreWithScale
                                    className='testACTReport__scoreWithScale'
                                    score={Math.round(testData.compositeScore)}
                                    scoreMin={Math.round(testData.minScore)}
                                    scoreMax={Math.round(testData.maxScore)}
                                    fontSize={70}
                                    // fontSize={windowSize.width > 576
                                    //     ? 100
                                    //     : 70
                                    // }
                                />
                                <div className="testACTReport__graphWrap">
                                    <div className="testACTReport__graphLocation" style={{ left: `${((Math.round(testData.compositeScore) - 1) / 35) * 100}%` }}>
                                        <div className="testACTReport__graph">you</div>
                                        <div className="triangle"></div>
                                    </div>
                                    <div className="testACTReport__graphBar" />
                                    <div className="testACTReport__maxScore">
                                        {Math.round(testData.maxScore)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>}
                    {testData.stemScore && <>
                        <div className='testACTReport__scoresGroupBoxStem'>
                            <div className='testACTReport__scoreTitleBoxStem'>
                                <h3 className='testACTReport__scoreTitle'>STEM SCORE</h3>
                            </div>
                            <div className='testACTReport__scoreContent'>
                                <ScoreWithScale
                                    className='testACTReport__scoreWithScale'
                                    score={Math.round(testData.stemScore)}
                                    scoreMin={Math.round(testData.minScore)}
                                    scoreMax={Math.round(testData.maxScore)}
                                    fontSize={70}
                                    // fontSize={windowSize.width > 576
                                    //     ? 100
                                    //     : 70
                                    // }
                                />
                                <div className="testACTReport__graphWrap">
                                    <div className="testACTReport__graphLocation" style={{ left: `${((Math.round(testData.stemScore) - 1) / 35) * 100}%` }}>
                                        <div className="testACTReport__graph">you</div>
                                        <div className="triangle"></div>
                                    </div>
                                    <div className="testACTReport__graphBarStem" />
                                    <div className="testACTReport__maxScore">
                                        {Math.round(testData.maxScore)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>}
                </div>

                {/* subject info */}
                {testData.testSections.map((testSection, s) => {
                    const testModule = testData.testModules.find(module => module.subject === testSection.subject);
                    const correctCount = testModule ? testModule.answers.filter(answer => answer.isCorrect).length : '-';
                    const totalCount = testModule ? testModule.answers.length : '-';
                    const timeSpent = testModule ? testModule.answers.reduce((total, answer) => total + answer.timeSpent, 0) : null;

                    return (
                        <div className='testACTReport__insideCard'>
                            <div className='testACTReport__ScoreBox'>
                                <div className='testACTReport__sectionScore'>
                                    <h3 className='testACTReport__sectionScoreTitle'>
                                        {testSection.subject.replace('ACT ', '').replace('Mathematics', 'Math')}
                                    </h3>
                                    <ScoreWithScale
                                        className='testACTReport__scoreWithScale'
                                        score={Math.round(testSection.score)}
                                        scoreMin={Math.round(testSection.minScore)}
                                        scoreMax={Math.round(testSection.maxScore)}
                                        fontSize={fontSize}
                                    />
                                </div>
                                <div className='testACTReport__rowScore'>
                                    <div className='testACTReport__infoTitle'>Raw score</div>
                                    <table className='testACTReport__overviewTable'>
                                        <tbody>
                                            <tr>
                                                <th>Correct</th>
                                                <td>{correctCount}/{totalCount}</td>
                                            </tr>
                                            <tr>
                                                <th>Time</th>
                                                <td>{timeSpent !== null ? formatTime(timeSpent) : '-'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className='testACTReport__sectionInfo'>
                                <table className='testACTReport__categoryInfo'>
                                    <tr>
                                        <th>By Category</th>
                                        <th>Section Time</th>
                                        <th>Time Per Question</th>
                                    </tr>
                                    {testSection.rootCategories.map(rootCategory => (<>
                                        <tr>
                                            <td>
                                                <h5 className='testACTReport__levelLabel'>
                                                    {rootCategory.name}
                                                </h5>
                                                <LevelBar
                                                    className={testSection.subject==='ACT English'
                                                        ? 'LevelBar__greenBox'
                                                        : testSection.subject==='ACT Mathematics'
                                                        ? 'LevelBar__orangeBox'
                                                        : testSection.subject==='ACT Reading'
                                                        ? 'LevelBar__redBox'
                                                        : 'LevelBar__blueBox'
                                                        } 
                                                    percent={100*rootCategory.numCorrect/rootCategory.numQuestions}
                                                    label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                                />
                                            </td>
                                            <td>{formatTime2(rootCategory.timeSpent)}</td>
                                            <td>
                                                <GaugeComponent
                                                    className='testACTReport__timeGauge'
                                                    type='semicircle'
                                                    value={rootCategory.timeSpent/rootCategory.numQuestions}
                                                    minValue={0}
                                                    maxValue={testSection.subject === 'ACT English'
                                                        ?   timePerQuestionEnglish * 3
                                                        :   testSection.subject === 'ACT Mathematics'
                                                        ?   timePerQuestionMath * 3
                                                        :   testSection.subject === 'ACT Reading'
                                                        ?   timePerQuestionReading * 3
                                                        :   testSection.subject === 'ACT Science'
                                                        ?   timePerQuestionScience * 3
                                                        :   180
                                                    }
                                                    arc={{
                                                        'subArcs': [
                                                            { length: 0.33, color: "#5BE12C"},
                                                            { length: 0.33, color: "#F5CD19"},
                                                            { length: 0.33, color: "#EA4228"}
                                                        ]
                                                    }}
                                                    pointer={{
                                                        'width': 10,
                                                    }}
                                                    labels={{
                                                        'valueLabel': {
                                                            'hide': true
                                                        },
                                                        'tickLabels': {
                                                            'hideMinMax': true
                                                        }
                                                    }}
                                                />
                                                <p>{formatTime2(rootCategory.timeSpent/rootCategory.numQuestions)}</p>
                                            </td>
                                        </tr>
                                    </>))}
                                </table>
                            </div>
                        </div>
                    )
                })}
            </div>
            
            <div className='testACTReport__printBreak'></div>

            {/* question overview */}
            <div className='testACTReport__questions'>
                <h2 className='testACTReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='testACTReport__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'testACTReport__sectionTab--active'
                                : 'testACTReport__sectionTab'
                            }
                            onClick={() => {
                                setSectionIndex(t);
                                setModuleIndex(0);
                            }}
                            key={testSection.subject}
                        >
                            {testSection.subject.replace('ACT ', '')}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'testACTReport__sectionTabIndicatorLeft'
                        : sectionIndex === 1
                        ? 'testACTReport__sectionTabIndicatorMiddle'
                        : sectionIndex === 2
                        ? 'testACTReport__sectionTabIndicatorMiddle2'
                        : 'testACTReport__sectionTabIndicatorRight'
                    }></div>
                </div>
                {testData.testModules.filter(testModule => (
                    testModule.subject === testData.testSections[sectionIndex].subject
                )).length > 1 &&
                    <div className='testACTReport__moduleTabs'>
                        {testData.testModules.filter(testModule => (
                            testModule.subject === testData.testSections[sectionIndex].subject
                        )).map((testModule, t) => (
                            <button
                                className={moduleIndex === t
                                    ? 'testACTReport__moduleTab--active'
                                    : 'testACTReport__moduleTab'
                                }
                                onClick={() => setModuleIndex(t)}
                                key={testModule.name}
                            >
                                Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                            </button>
                        ))}
                        <div className={moduleIndex === 0
                            ? 'testACTReport__moduleTabIndicatorLeft'
                            : 'testACTReport__moduleTabIndicatorRight'
                        }></div>
                    </div>
                }
                
                <div className='testACTReport__questionsOverview'>
                    <div className='testACTReport__questionsSummary'>
                        <div className='testACTReport__questionsSummaryItem'>
                            <p className='testACTReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.length
                                ), 0)}
                            </p>
                            <p className='testACTReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='testACTReport__questionsSummaryItem'>
                            <p className='testACTReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='testACTReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='testACTReport__questionsSummaryItem'>
                            <p className='testACTReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => !answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='testACTReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    
                    <table className='testACTReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                <th>Correct Answer</th>
                                <th>Your Answer</th>
                                <th>Category</th>
                                {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                    <th>Correct %</th>
                                }
                                <th>Time Spent</th>
                            </tr>
                            {(() => {
                                const currentModule = testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                ))[moduleIndex];
                                return currentModule.answers.map(answer => (
                                <tr key={currentModule.name + answer.questionNumber}>
                                    <td>{answer.questionNumber}</td>
                                    <td>{answer.correctChoice !== null
                                        ?   MULTIPLE_CHOICES[answer.correctChoice]
                                        :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                    }</td>
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='testACTReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='testACTReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='testACTReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>{answer.category}</td>
                                    {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                        <td>{answer.correctRate ? `${parseFloat(answer.correctRate).toFixed(1)}%` : '-'}</td>
                                    }
                                    <td>
                                    <p className={answer.timeSpent > (currentModule.subject === 'ACT English'
                                                    ? timePerQuestionEnglish * 1.5
                                                    : currentModule.subject === 'ACT Mathematics'
                                                    ? timePerQuestionMath * 1.5
                                                    : currentModule.subject === 'ACT Reading'
                                                    ? timePerQuestionReading * 1.5
                                                    : currentModule.subject === 'ACT Science'
                                                    ? timePerQuestionScience * 1.5
                                                    : 60 * 1.5
                                                )
                                            ?   'testACTReport__timeSpentBad'
                                            :   answer.timeSpent > (currentModule.subject === 'ACT English'
                                                    ? timePerQuestionEnglish
                                                    : currentModule.subject === 'ACT Mathematics'
                                                    ? timePerQuestionMath
                                                    : currentModule.subject === 'ACT Reading'
                                                    ? timePerQuestionReading
                                                    : currentModule.subject === 'ACT Science'
                                                    ? timePerQuestionScience
                                                    : 60
                                                )
                                            ?   'testACTReport__timeSpentWarning'
                                            :   'testACTReport__timeSpentGood'
                                        }>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ));
                            })()}
                        </tbody>
                    </table>
                </div>
            </div>

            {/*
                Print-only: every section on its own sheet, split into two
                side-by-side halves so a 50-question section fits one page. No
                heading and no Correct % column, to keep the halves readable
                at that density.
                Hidden in print by default and revealed only for a
                single-report print (the `printing-single-report` rules in
                index.css), so a bulk print stays overview-only.
            */}
            <div className='testACTReport__questionsPrint'>
                {testData.testModules.map(testModule => {
                    const half = Math.ceil(testModule.answers.length / 2);
                    const timeLimit = testModule.subject === 'ACT English'
                        ? timePerQuestionEnglish
                        : testModule.subject === 'ACT Mathematics'
                        ? timePerQuestionMath
                        : testModule.subject === 'ACT Reading'
                        ? timePerQuestionReading
                        : testModule.subject === 'ACT Science'
                        ? timePerQuestionScience
                        : 60;
                    return (
                        <div className='testACTReport__questionsPrintModule' key={`print:${testModule.name}`}>
                            <p className='testACTReport__moduleName'>
                                {testModule.subject.replace('ACT ', '')}
                            </p>
                            <div className='testACTReport__questionsTableContainer'>
                                {[testModule.answers.slice(0, half), testModule.answers.slice(half)].map((answers, h) => (
                                    <table className='testACTReport__questionsTable' key={h}>
                                        <thead>
                                            <tr>
                                                <th>Q</th>
                                                <th>Correct Answer</th>
                                                <th>Your Answer</th>
                                                <th>Category</th>
                                                <th>Time Spent</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {answers.map(answer => (
                                                <tr key={testModule.name + answer.questionNumber}>
                                                    <td>{answer.questionNumber}</td>
                                                    <td>{answer.correctChoice !== null
                                                        ?   MULTIPLE_CHOICES[answer.correctChoice]
                                                        :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                                    }</td>
                                                    <td>{answer.answer !== null
                                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                                ? <p>{answer.answer}; Correct</p>
                                                                : <p className='testACTReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                                            )
                                                        :   answer.choice !== null
                                                        ?   (answer.choice === answer.correctChoice
                                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                                : <p className='testACTReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                                            )
                                                        :   <p className='testACTReport__questionOmitted'>Omitted</p>
                                                    }</td>
                                                    <td>{answer.category}</td>
                                                    <td>
                                                        <p className={answer.timeSpent > timeLimit * 1.5
                                                            ?   'testACTReport__timeSpentBad'
                                                            :   answer.timeSpent > timeLimit
                                                            ?   'testACTReport__timeSpentWarning'
                                                            :   'testACTReport__timeSpentGood'
                                                        }>
                                                            {formatTime(answer.timeSpent)}
                                                        </p>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default DigitalACTReport;